import { Directive, Host, HostListener, Input, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';
import { DecimalService } from '../service/decimal.service';

// 正整數
@Directive({
  selector: '[input-number]',
  standalone: true,
})
export class InputNumberDirective {
  constructor(@Host() @Optional() private control: NgControl) {}

  @HostListener('input', ['$event.target', '$event.target.value'])
  onInput(el: HTMLInputElement, value: string): void {
    const newVal = el.value
      .replace(/\D/g, '')
      .replace(/^0+$/, '0')
      .replace(/^0(\d+)$/, '$1');

    if (newVal === value) return;

    el.value = newVal;
    this.control?.control?.setValue(newVal);
    // 重新觸發 'input' 事件，以確保變更被其他監聽者捕獲
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

// 小數
@Directive({
  selector: '[input-float]',
  standalone: true,
})
export class InputFloatDirective {
  constructor(
    @Host() @Optional() private control: NgControl,
    private decimalService: DecimalService,
  ) {}

  /**
   * 設定允許的最大數值。
   */
  @Input() max: number | undefined;

  /**
   * 設定允許的最大數值。
   */
  @Input() min: number | undefined;

  /**
   * 設定允許小數點後的位數。
   */
  @Input() decimals: number | undefined;

  @HostListener('input', ['$event.target'])
  onInput(el: HTMLInputElement) {
    const originalValue = el.value;

    // 0. 統一「數字表示法」使用國際化表示
    // 國際化： 小數點為點 ( . )，千分位為逗號 ( , )
    // 歐洲： 小數點為逗號 ( , )，千分位為點 ( . )
    let newValue = originalValue.replace(/,/g, '.');

    // 1. 過濾掉所有非數字和非小數點的字元
    newValue =
      this.decimals !== undefined && this.decimals <= 0
        ? newValue.replace(/[^0-9]/g, '')
        : newValue.replace(/[^0-9.]/g, '');

    // 2. 處理多個小數點的情況，只保留第一個
    const parts = newValue.split('.');
    if (parts.length > 2) {
      newValue = parts[0] + '.' + parts.slice(1).join('');
    }

    // 3. 更新：處理整數的前導零 (例如 "01" -> "1")，
    //    但保留 "0.5" 這種格式，並且不影響小數點後使用者輸入的零 (例如 "1.50")
    if (parts[0].length > 1 && parts[0].startsWith('0')) {
      const integerPart = parts[0];
      const newIntegerPart = parseInt(integerPart, 10).toString();
      newValue = newIntegerPart + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
    }

    // 4. 如果值被修改了，則更新 input 和 form control
    if (newValue !== originalValue) {
      this.updateInputValue(el, newValue);
    }
  }

  @HostListener('inputBlur', ['$event.target'])
  onBlur(el: HTMLInputElement) {
    let value = this.decimalService.toFixed(el.value);

    // 如果值是空的，則不處理
    if (!value) {
      // 如果輸入是無效的 (例如只有一個 "."), 則清空
      if (el.value) this.updateInputValue(el, '');
      return;
    }

    // 如果有設定小數點位數，則限制小數點後的位數
    if (this.decimals !== undefined) {
      const parts = value.split('.');
      parts[1] = parts[1]?.slice(0, this.decimals);
      const newValue = parts.filter(part => part !== undefined).join('.');
      value = this.decimalService.toFixed(newValue);
    }

    let numericValue = value;

    // 檢查是否超過最大值
    if (this.decimalService.isValid(this.max)) {
      const max = this.decimalService.toFixed(this.max);
      const isGreaterThenMax = this.decimalService.isGreaterThan(numericValue, max);
      numericValue = isGreaterThenMax ? max : numericValue; // 如果超過最大值，就設定為最大值
    }

    // 檢查是否超過最小值
    if (this.decimalService.isValid(this.min)) {
      const min = this.decimalService.toFixed(this.min);
      const isLessThenMax = this.decimalService.isLessThan(numericValue, min);
      numericValue = isLessThenMax ? min : numericValue; // 如果超過最小值，就設定為最小值
    }

    // 只有在值確實發生變化時才更新
    if (numericValue !== el.value) {
      this.updateInputValue(el, numericValue);
    }
  }

  private updateInputValue(el: HTMLInputElement, value: string): void {
    el.value = value;
    this.control?.control?.setValue(value);

    // 重新觸發 'input' 事件，以確保變更被其他監聽者捕獲
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
